/* eslint-disable no-console */
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { useHistory } from "react-router-dom";
import supabase from "../services/supabaseClient";
import { firestore, geo, imageRef, storage } from "../services/firebase";
import { debounce } from "../lib";
import { useUserRoles } from "../globalstore";
import {
	formatTimeForStorage,
	normalizeDayHours
} from "../lib/hours";

const BarsContext = React.createContext();
const SelectedBarContext = React.createContext();
const BarUtilsContext = React.createContext();

const DAYS = [
	"sunday",
	"monday",
	"tuesday",
	"wednesday",
	"thursday",
	"friday",
	"saturday"
];

const mapCategoriesRow = (row) => {
	if (!row || typeof row !== "object") {
		return {};
	}

	const result = {};
	DAYS.forEach((day) => {
		result[day] = row[day] || {};
	});
	return result;
};

const serializeHours = (hours) => {
		if (!hours || typeof hours !== "object") {
			return {};
		}

		const result = {};

		if (typeof hours.raw_opening_hours === "string") {
			result.raw_opening_hours = hours.raw_opening_hours;
		}

		if (typeof hours.parsed === "boolean") {
			result.parsed = hours.parsed;
		}

		DAYS.forEach((day) => {
			const value = hours[day] || {};
			const openTime = formatTimeForStorage(value.openTime ?? value.open_time);
			const closeTime = formatTimeForStorage(
				value.closeTime ?? value.close_time,
				openTime ?? value.openTime ?? value.open_time
			);
			result[day] = {
				isOpen: Boolean(value.isOpen ?? value.is_open ?? false),
				openTime,
				closeTime
			};
		});

		return result;
	};

const normalizeHours = (hours) => {
		if (!hours || typeof hours !== "object") {
			return {};
		}

		const normalized = {};

		if (typeof hours.raw_opening_hours === "string") {
			normalized.raw_opening_hours = hours.raw_opening_hours;
		}

		if (hours.parsed !== undefined) {
			normalized.parsed = Boolean(hours.parsed);
		}

		DAYS.forEach((day) => {
			const value = hours[day] || {};
			normalized[day] = normalizeDayHours(value);
		});

		return normalized;
	};

const parseCityFromAddress = (address) => {
	if (!address) {
		return "";
	}

	if (Array.isArray(address)) {
		const candidate = address[1];
		if (candidate && typeof candidate === "string") {
			return candidate.trim();
		}
		return address.find((value) => typeof value === "string" && value.trim())?.trim() || "";
	}

	if (typeof address !== "string") {
		return "";
	}

	const segments = address
		.split(",")
		.map((segment) => segment.trim())
		.filter(Boolean);

	if (segments.length >= 2) {
		return segments[segments.length - 2];
	}

	return segments[0] || "";
};

const parseStateFromAddress = (address) => {
	if (!address) {
		return "";
	}

	if (Array.isArray(address)) {
		const candidate = address[2];
		if (typeof candidate === "string") {
			return candidate.trim();
		}
		return "";
	}

	if (typeof address !== "string") {
		return "";
	}

	const segments = address
		.split(",")
		.map((segment) => segment.trim())
		.filter(Boolean);

	if (!segments.length) {
		return "";
	}

	const trail = segments[segments.length - 1];
	const stateMatch = trail.match(/([A-Za-z]{2})\s+\d{5}/);
	if (stateMatch && stateMatch[1]) {
		return stateMatch[1];
	}

	const tokens = trail.split(" ").filter(Boolean);
	return tokens[0] || "";
};

const parsePostalCodeFromAddress = (address) => {
	if (!address) {
		return "";
	}

	if (Array.isArray(address)) {
		const candidate = address[3];
		if (typeof candidate === "string") {
			return candidate.trim();
		}
		return "";
	}

	if (typeof address !== "string") {
		return "";
	}

	const zipMatch = address.match(/\b(\d{5})(?:-\d{4})?\b/);
	return zipMatch ? zipMatch[1] : "";
};

const parseCoordinate = (value) => {
	if (value === null || value === undefined) {
		return null;
	}

	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}

	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

const mapSupabaseBar = (row, categoriesRow) => {
	if (!row) {
		return null;
	}

	let latitude = parseCoordinate(row.latitude);
	let longitude = parseCoordinate(row.longitude);

	if ((latitude === null || longitude === null) && row.location) {
		try {
			if (
				Array.isArray(row.location?.coordinates) &&
				row.location.coordinates.length >= 2
			) {
				longitude = parseCoordinate(row.location.coordinates[0]);
				latitude = parseCoordinate(row.location.coordinates[1]);
			} else if (typeof row.location === "string") {
				// Expected formats like 'POINT(lon lat)' or GeoJSON string
				const pointMatch = row.location.match(/POINT\s*\(([-\d.]+)\s+([-\d.]+)\)/i);
				if (pointMatch) {
					longitude = parseCoordinate(pointMatch[1]);
					latitude = parseCoordinate(pointMatch[2]);
				} else {
					const parsed = JSON.parse(row.location);
					if (Array.isArray(parsed?.coordinates) && parsed.coordinates.length >= 2) {
						longitude = parseCoordinate(parsed.coordinates[0]);
						latitude = parseCoordinate(parsed.coordinates[1]);
					}
				}
			}
		} catch (error) {
			console.debug("parse location fallback failed", error);
		}
	}

	const position =
		latitude !== null && longitude !== null
			? {
				geopoint: {
					latitude,
					longitude
				},
				geohash: row.geohash || null
			}
			: null;

	const barImages = Array.isArray(row.bar_images) ? row.bar_images : [];
	const activePromotionsSummary = Array.isArray(row.active_promotions_summary)
		? row.active_promotions_summary
		: [];

	const osmMetadata = row.osm_metadata && typeof row.osm_metadata === "object"
		? row.osm_metadata
		: {};

	const metadataCity = (osmMetadata.city ?? osmMetadata.town ?? osmMetadata.village ?? "");
	const addressCity = parseCityFromAddress(row.bar_address);
	const city = (metadataCity || addressCity || "").trim();
	const metadataState = (osmMetadata.state ?? osmMetadata.region ?? "");
	const state = (metadataState || parseStateFromAddress(row.bar_address) || "").trim();
	const postalCode = (osmMetadata.postcode ?? parsePostalCodeFromAddress(row.bar_address) ?? "").trim();

	return {
		id: row.bar_id,
		barId: row.bar_id,
		barName: row.bar_name,
		barNameLower: row.bar_name_lower,
		barDescription: row.bar_description,
		barAddress: row.bar_address,
		barEmail: row.bar_email,
		barPhone: row.bar_phone,
		barUrl: row.bar_url,
		googlePlaceId: row.google_place_id,
		googleLastSyncedAt: row.google_last_synced_at,
		barCoverImage: row.bar_cover_image,
		barImages,
		barType: row.bar_type,
		createdBy: row.created_by,
		ownerEmail: row.owner_email,
		position,
		latitude,
		longitude,
		geohash: row.geohash || null,
		activePromotionCount: row.active_promotion_count || 0,
		activePromotionsSummary,
		hasActivePromotion: Boolean(row.has_active_promotion),
		lastPromotionUpdateUTC: row.last_promotion_update_utc,
		nextPromotionExpiryUTC: row.next_promotion_expiry_utc,
		crowdCount: row.crowd_count || {},
		hours: normalizeHours(row.hours),
		osmMetadata,
		dataSource: row.data_source,
		hasChanged: Boolean(row.has_changed),
		createdAt: row.created_at,
		updatedAt: row.updated_at,
		categories: mapCategoriesRow(categoriesRow),
		regionId: osmMetadata.region_id || null,
		city: city || "",
		cityLower: city ? city.toLowerCase() : "",
		state: state || "",
		postalCode: postalCode || ""
	};
};

const buildCategoriesPayload = (barId, categories) => {
	const payload = { bar_id: barId };
	DAYS.forEach((day) => {
		payload[day] = categories?.[day] || {};
	});
	return payload;
};

const mergeRegionIntoMetadata = (metadata, regionId) => {
	const base = metadata && typeof metadata === "object" ? metadata : {};
	return {
		...base,
		region_id: regionId
	};
};

const sanitizePayload = (payload) =>
	Object.fromEntries(
		Object.entries(payload).filter(([, value]) => value !== undefined)
	);

export function BarsProvider({ numResults = 100, children }) {
	const history = useHistory();
	const {
		userRoles: { id: userid }
	} = useUserRoles();

	const [bars, setBars] = useState([]);
	const [selectedBar, setSelectedBarState] = useState(null);
	const [endOfResults, setEndOfResults] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [loading, setLoading] = useState(false);

	const isFetchingRef = useRef(false);
	const nextOffsetRef = useRef(0);

	const replaceBarInList = useCallback((updatedBar) => {
		if (!updatedBar?.barId) {
			return;
		}

		setBars((current) => {
			const index = current.findIndex((bar) => bar.barId === updatedBar.barId);
			if (index === -1) {
				return current;
			}
			const clone = [...current];
			clone[index] = updatedBar;
			return clone;
		});
	}, []);

	const loadBars = useCallback(
		async ({ reset = false, search } = {}) => {
			if (isFetchingRef.current) {
				return;
			}

			isFetchingRef.current = true;
			setLoading(true);
			const currentSearch = typeof search === "string" ? search : searchTerm;

			try {
				let data = [];

				if (currentSearch) {
					const { data: rows, error } = await supabase.rpc(
						"search_bars_by_name",
						{
							p_search: currentSearch,
							p_limit: numResults
						}
					);

					if (error) {
						console.error("search_bars_by_name failed", error);
						recordTelemetry("search_bars_by_name", error);
						return;
					}

					data = rows || [];
					setEndOfResults(true);
					nextOffsetRef.current = data.length;
				} else {
					const offset = reset ? 0 : nextOffsetRef.current;
					const from = offset;
					const to = offset + numResults - 1;
					const { data: rows, error } = await supabase
						.from("bars")
						.select("*")
						.order("bar_name_lower", { ascending: true })
						.range(from, to);

					if (error) {
						console.error("bars fetch failed", error);
						recordTelemetry("list_bars", error);
						return;
					}

					data = rows || [];
					const reachedEnd = data.length < numResults;
					setEndOfResults(reachedEnd);
					nextOffsetRef.current = offset + data.length;
				}

				const mapped = data
					.map((row) => mapSupabaseBar(row))
					.filter(Boolean);

				setBars((current) => (reset ? mapped : [...current, ...mapped]));
			} finally {
				isFetchingRef.current = false;
				setLoading(false);
			}
		},
		[numResults, searchTerm]
	);

	useEffect(() => {
		loadBars({ reset: true });
	}, [loadBars]);

	const initialFetch = useCallback(
		(search) => {
			setEndOfResults(false);
			nextOffsetRef.current = 0;
			loadBars({ reset: true, search });
		},
		[loadBars]
	);

	const getNext = useCallback(
		(search) => {
			if (endOfResults) {
				return;
			}
			loadBars({ search });
		},
		[endOfResults, loadBars]
	);

	const resetAndFetch = useCallback(() => {
		setBars([]);
		initialFetch(searchTerm);
	}, [initialFetch, searchTerm]);

	const setSearch = useCallback(
		(text) => {
			setEndOfResults(false);
			nextOffsetRef.current = 0;
			setSearchTerm(text);
			loadBars({ reset: true, search: text });
		},
		[loadBars]
	);

	const debouncedSetBarsQuery = useMemo(
		() => debounce(setSearch, 250),
		[setSearch]
	);

	const clearSelectedBar = useCallback(() => {
		setSelectedBarState(null);
	}, []);

	const fetchBarCategories = useCallback(async (barId) => {
		const { data, error } = await supabase
			.from("bar_categories_prod")
			.select("*")
			.eq("bar_id", barId)
			.maybeSingle();

		if (error && error.code !== "PGRST116") {
			console.error("fetchBarCategories failed", error);
			recordTelemetry("fetch_bar_categories", error);
		}

		return data || null;
	}, []);

	const fetchBarById = useCallback(
		async (barId) => {
			if (!barId) {
				return null;
			}

			const [{ data: barRows, error }, categoriesRow] = await Promise.all([
				supabase.rpc("get_bar_by_id", { p_bar_id: barId }),
				fetchBarCategories(barId)
			]);

			if (error) {
				console.error("get_bar_by_id failed", error);
				recordTelemetry("get_bar_by_id", error);
				return null;
			}

			const row = Array.isArray(barRows) ? barRows[0] : null;
			const mapped = mapSupabaseBar(row, categoriesRow);

			if (!mapped) {
				return null;
			}

			setSelectedBarState(mapped);
			replaceBarInList(mapped);
			return mapped;
		},
		[fetchBarCategories, replaceBarInList]
	);

	const selectBar = useCallback(
		(bar) => {
			if (!bar?.barId) {
				setSelectedBarState(null);
				return;
			}
			setSelectedBarState(bar);
			fetchBarById(bar.barId);
		},
		[fetchBarById]
	);

	const buildBarPayload = useCallback((bar, id) => {
		const barImages = Array.isArray(bar.barImages)
			? bar.barImages.filter(Boolean)
			: [];
		const hours = serializeHours(bar.hours);
		const hasValidCoordinates =
			typeof bar.latitude === "number" && typeof bar.longitude === "number";

		const payload = {
			bar_id: id,
			bar_name: bar.barName,
			bar_name_lower: bar.barName ? bar.barName.toLowerCase() : null,
			bar_description: bar.barDescription,
			bar_address: bar.barAddress,
			bar_email: bar.barEmail || null,
			bar_phone: bar.barPhone,
			bar_url: bar.barUrl,
			bar_cover_image: bar.barCoverImage || null,
			bar_images: barImages,
			bar_type: bar.barType || null,
			created_by: bar.createdBy || null,
			owner_email: bar.ownerEmail || null,
			geohash: hasValidCoordinates
				? geo.point(Number(bar.latitude), Number(bar.longitude)).hash
				: null,
			hours,
			google_place_id: bar.googlePlaceId || null,
			google_last_synced_at: bar.googleLastSyncedAt || null,
			data_source: bar.dataSource || "admin",
			osm_metadata: {
				...(bar.osmMetadata || {}),
				...(bar.regionId ? { region_id: bar.regionId } : {})
			}
		};

		if (hasValidCoordinates) {
			payload.location = {
				type: "Point",
				coordinates: [Number(bar.longitude), Number(bar.latitude)]
			};
		}

		return sanitizePayload(payload);
	}, []);

	const saveBar = useCallback(
		async (barId, bar) => {
			const isNew = barId === "new";
			const id = isNew ? generateId() : barId;
			const payload = buildBarPayload(bar, id);

			const { error } = await supabase
				.from("bars")
				.upsert(payload, { onConflict: "bar_id" });

			if (error) {
				console.error("saveBar upsert failed", error);
				recordTelemetry("save_bar", error);
				throw error;
			}

			const updatedBar = await fetchBarById(id);
			if (!updatedBar) {
				return { id, isNew };
			}

			if (isNew) {
				await firestore.collection("Users").doc(userid).update({
					[`roles.${id}`]: {
						barName: updatedBar.barName,
						role: "owner"
					}
				});

				history.push(`/bar/${id}`);
			}

			setBars((current) => {
				const exists = current.some((item) => item.barId === updatedBar.barId);
				if (exists) {
					return current.map((item) =>
						item.barId === updatedBar.barId ? updatedBar : item
					);
				}
				return [updatedBar, ...current];
			});

			return { id, isNew };
		},
		[buildBarPayload, fetchBarById, history, userid]
	);

	const saveBarCategories = useCallback(
		async (categories) => {
			if (!selectedBar?.barId) {
				return;
			}

			const payload = buildCategoriesPayload(selectedBar.barId, categories);
			const { error } = await supabase
				.from("bar_categories_prod")
				.upsert(payload, { onConflict: "bar_id" });

			if (error) {
				console.error("saveBarCategories failed", error);
				recordTelemetry("save_bar_categories", error);
				throw error;
			}

			await fetchBarById(selectedBar.barId);
		},
		[fetchBarById, selectedBar?.barId]
	);

	const assignRegionsToBars = useCallback(
		async (barIds, regionId) => {
			if (!Array.isArray(barIds) || barIds.length === 0) {
				return { updated: 0 };
			}

			if (!regionId) {
				throw new Error("Region is required to assign");
			}

			try {
				const { data: rows, error: fetchError } = await supabase
					.from("bars")
					.select("bar_id, bar_name, osm_metadata")
					.in("bar_id", barIds);

				if (fetchError) {
					console.error("assignRegionsToBars fetch failed", fetchError);
					recordTelemetry("assign_regions_fetch", fetchError);
					throw fetchError;
				}

				const validRows = (rows || []).filter(
					(row) => row && row.bar_id && row.bar_name
				);

				if (validRows.length === 0) {
					throw new Error("No matching bars found to assign region");
				}

				const payload = validRows.map((row) => ({
					bar_id: row.bar_id,
					bar_name: row.bar_name,
					osm_metadata: mergeRegionIntoMetadata(row.osm_metadata, regionId)
				}));

				const { error: upsertError } = await supabase
					.from("bars")
					.upsert(payload, { onConflict: "bar_id" });

				if (upsertError) {
					console.error("assignRegionsToBars upsert failed", upsertError);
					recordTelemetry("assign_regions_upsert", upsertError);
					throw upsertError;
				}

				const updatedIds = payload.map((row) => row.bar_id);
				setBars((current) =>
					current.map((bar) => {
						if (!updatedIds.includes(bar.barId)) {
							return bar;
						}
						return {
							...bar,
							regionId,
							osmMetadata: mergeRegionIntoMetadata(bar.osmMetadata, regionId)
						};
					})
				);

				return { updated: payload.length };
			} catch (error) {
				console.error("assignRegionsToBars failed", error);
				recordTelemetry("assign_regions", error);
				throw error;
			}
		},
		[]
	);

	const uploadImage = useCallback(
		async (barId, bar, file) => {
			const name = generateId();
			const fileName = `${name}.jpeg`;
			const response = await imageRef.child(`${barId}/${fileName}`).put(file);
			const newImageUrlPromise = await response.ref.getDownloadURL();

			const updatedImages = Array.isArray(bar.barImages)
				? [...bar.barImages, fileName]
				: [fileName];

			const { error } = await supabase
				.from("bars")
				.update({ bar_images: updatedImages })
				.eq("bar_id", barId);

			if (error) {
				console.error("uploadImage failed", error);
				recordTelemetry("upload_bar_image", error);
				throw error;
			}

			await fetchBarById(barId);

			return {
				fileName,
				newImageUrlPromise
			};
		},
		[fetchBarById]
	);

	const deleteImage = useCallback(
		(imageUrl) => {
			if (!selectedBar?.barId) {
				return Promise.resolve();
			}

			return new Promise((resolve) => {
				const imgRef = storage.refFromURL(imageUrl);

				imgRef
					.delete()
					.then(async () => {
						const fileName = imgRef.fullPath.split("/").pop();
						const remainingImages = (selectedBar.barImages || []).filter(
							(name) => name !== fileName
						);

						const updates = {
							bar_images: remainingImages,
							bar_cover_image:
								selectedBar.barCoverImage === fileName
									? remainingImages[0] || null
								: selectedBar.barCoverImage
						};

						const { error } = await supabase
							.from("bars")
							.update(updates)
							.eq("bar_id", selectedBar.barId);

						if (error) {
							console.error("deleteImage update failed", error);
							recordTelemetry("delete_bar_image", error);
						}

						await fetchBarById(selectedBar.barId);
						resolve();
					})
					.catch((err) => {
						console.error(err);
						resolve(err);
					});
			});
		},
		[fetchBarById, selectedBar]
	);

	const markAsBannerImage = useCallback(
		async (imageUrl, isBanner) => {
			if (!selectedBar?.barId) {
				return;
			}

			const imgRef = storage.refFromURL(imageUrl);
			const fileName = isBanner ? "" : imgRef.fullPath.split("/").pop();

			const { error } = await supabase
				.from("bars")
				.update({ bar_cover_image: fileName || null })
				.eq("bar_id", selectedBar.barId);

			if (error) {
				console.error("markAsBannerImage failed", error);
				recordTelemetry("mark_bar_banner_image", error);
				throw error;
			}

			await fetchBarById(selectedBar.barId);
		},
		[fetchBarById, selectedBar?.barId]
	);

	const contextValue = useMemo(
		() => ({ bars, endOfResults, loading }),
		[bars, endOfResults, loading]
	);

	const utilsValue = useMemo(
		() => ({
			initialFetch,
			getNext,
			setBarsQuery: debouncedSetBarsQuery,
			refreshSearch: resetAndFetch,
			fetchBarById,
			saveBar,
			setSelectedBar: selectBar,
			saveBarCategories,
			assignRegionsToBars,
			uploadImage,
			deleteImage,
			markAsBannerImage,
			clearSelectedBar
		}),
		[
			clearSelectedBar,
			assignRegionsToBars,
			debouncedSetBarsQuery,
			deleteImage,
			fetchBarById,
			getNext,
			initialFetch,
			markAsBannerImage,
			resetAndFetch,
			saveBar,
			saveBarCategories,
			selectBar,
			uploadImage
		]
	);

	return (
		<BarUtilsContext.Provider value={utilsValue}>
			<BarsContext.Provider value={contextValue}>
				<SelectedBarContext.Provider value={selectedBar}>
					{children}
				</SelectedBarContext.Provider>
			</BarsContext.Provider>
		</BarUtilsContext.Provider>
	);
}

export const useBars = () => useContext(BarsContext);
export const useBarUtils = () => useContext(BarUtilsContext);
export const useSelectedBar = () => useContext(SelectedBarContext);

export function asyncGetBarImages(id, bar) {
	if (!bar?.barImages) {
		return null;
	}

	const barImageNames = bar.barImages;
	const urls = barImageNames.map((name) =>
		imageRef.child(`${id}/${name}`).getDownloadURL()
	);

	return new Promise((resolve) => {
		Promise.all(urls).then(resolve);
	});
}

export const generateId = () => {
	const chars =
		"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
	let autoId = "";
	for (let i = 0; i < 20; i++) {
		autoId += chars.charAt(Math.floor(Math.random() * chars.length));
	}
	return autoId;
};

const recordTelemetry = (event, error) => {
	console.debug(event, error);
};
